import { useEffect, useState, type CSSProperties } from 'react';
import { ErreurApi } from '../../../api/client.js';
import { COULEURS, styleBouton, styleBoutonPrincipal } from '../../../app/styles.js';
import type { Compensations, QuestionBetaCompensation, SourceCompensations } from './source.js';

/**
 * Carte « Héritier sans argent : règlement en nature (foncier) » de la facturation du dossier (D08, PARITE-5, démo
 * isabelle/Compensations, RG-28), en LECTURE. L'avertissement et les pistes viennent de l'API (donnée versionnée, renonciation en
 * dernier) ; les sommes entre héritiers sont celles du moteur de facturation, marquées « Calcul à valider ». La règle du foncier
 * manque : la question BETA est posée ici, au moment où Isabelle ouvre la carte ; aucune compensation ne s'enregistre.
 */
export interface PropsCarteCompensations {
  source: SourceCompensations;
}

/** « 1 234,50 € » (espaces simples). */
const euros = (centimes: number): string => {
  const [e, c] = (centimes / 100).toFixed(2).split('.');
  return `${(e ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${c} €`;
};
const messageDe = (e: unknown): string => (e instanceof ErreurApi ? e.messageUtilisateur : 'Une erreur est survenue. Réessayez.');

const carte: CSSProperties = {
  background: COULEURS.blanc,
  border: '1px solid #E2E1EA',
  borderRadius: 12,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 0,
};
const discret: CSSProperties = { color: COULEURS.ardoise, fontSize: 13, margin: 0 };
const badge = (fond: string, encre: string): CSSProperties => ({
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 600,
  background: fond,
  color: encre,
  borderRadius: 999,
  padding: '2px 10px',
});

function QuestionBeta({
  question,
  onRepondre,
  onPlusTard,
}: {
  question: QuestionBetaCompensation;
  onRepondre: (option: string) => Promise<void>;
  onPlusTard: () => void;
}) {
  const [choix, setChoix] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  return (
    <div role="dialog" aria-labelledby="beta-compensation-titre" style={{ ...carte, borderColor: COULEURS.indigo }}>
      <p style={{ margin: 0, color: COULEURS.indigo, fontWeight: 600 }}>BETA test · question à Isabelle</p>
      <h3 id="beta-compensation-titre" style={{ margin: 0, fontSize: 16, color: COULEURS.marine }}>
        {question.question}
      </h3>
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <legend style={{ position: 'absolute', left: -9999 }}>{question.question}</legend>
        {question.options.map((o) => (
          <label key={o.code} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 }}>
            <input type="radio" name="beta-compensation" value={o.code} checked={choix === o.code} onChange={() => setChoix(o.code)} />
            {o.label}
          </label>
        ))}
      </fieldset>
      {erreur ? (
        <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
          {erreur}
        </p>
      ) : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button
          type="button"
          style={styleBoutonPrincipal}
          disabled={!choix || enCours}
          onClick={() => {
            setEnCours(true);
            setErreur(null);
            onRepondre(choix)
              .catch((e: unknown) => setErreur(messageDe(e)))
              .finally(() => setEnCours(false));
          }}
        >
          Enregistrer ma réponse
        </button>
        <button type="button" style={styleBouton} onClick={onPlusTard}>
          Plus tard
        </button>
      </div>
    </div>
  );
}

export function CarteCompensations({ source }: PropsCarteCompensations) {
  const [vue, setVue] = useState<Compensations | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [question, setQuestion] = useState<QuestionBetaCompensation | null>(null);

  useEffect(() => {
    let actif = true;
    source
      .lire()
      .then((v) => actif && setVue(v))
      .catch((e: unknown) => actif && setErreur(messageDe(e)));
    // Une question BETA ne gêne jamais le travail : en cas de panne, on ne pose rien.
    source
      .questions()
      .then((qs) => actif && setQuestion(qs[0] ?? null))
      .catch(() => actif && setQuestion(null));
    return () => {
      actif = false;
    };
  }, [source]);

  return (
    <section aria-label="Héritier sans argent : règlement en nature (foncier)" style={carte}>
      <h2 style={{ margin: 0, fontSize: 18, color: COULEURS.marine }}>Héritier sans argent : règlement en nature (foncier)</h2>
      <span style={{ ...badge('#EEF0F8', COULEURS.marine), alignSelf: 'flex-start' }}>visible : équipe MAAT</span>
      {erreur ? (
        <p role="alert" style={{ margin: 0, color: COULEURS.rouge, fontWeight: 600 }}>
          {erreur}
        </p>
      ) : null}
      {vue ? (
        <>
          <p data-testid="avertissement-renonciation" style={{ margin: 0 }}>
            <span style={badge(COULEURS.ambreFond, COULEURS.ambre)}>À retenir</span> {vue.warning}
          </p>
          <ol aria-label="Pistes proposées, dans l’ordre" style={{ margin: 0, paddingLeft: 20 }}>
            {vue.options.map((p) => (
              <li key={p.code}>
                <strong>{p.recommended ? `${p.label} (recommandé)` : p.label}</strong>
                {` : ${p.description}`}
              </li>
            ))}
          </ol>
          <div aria-label="Sommes entre héritiers" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <strong style={{ fontSize: 14 }}>
              Sommes entre héritiers <span style={badge(COULEURS.ambreFond, COULEURS.ambre)}>Calcul à valider</span>
            </strong>
            {vue.debts.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {vue.debts.map((d) => (
                  <li key={`${d.creditorPersonId}-${d.debtorPersonId}`}>
                    {`${d.debtorDisplayName} doit ${euros(d.amount.amountCents)} à ${d.creditorDisplayName} (avance sur sa part).`}
                  </li>
                ))}
              </ul>
            ) : (
              <p style={discret}>{vue.unavailableReason ?? 'Aucune somme entre héritiers.'}</p>
            )}
            <p style={discret}>{vue.calculation.note}</p>
            <p style={{ ...discret, fontStyle: 'italic' }}>{vue.calculation.mention}</p>
          </div>
          <p style={discret}>
            Lecture seule : aucune compensation ne s’enregistre ici tant que la règle du remboursement en foncier n’est pas décidée.
          </p>
        </>
      ) : erreur ? null : (
        <p role="status">Chargement…</p>
      )}
      {question ? (
        <QuestionBeta
          question={question}
          onPlusTard={() => setQuestion(null)}
          onRepondre={async (option) => {
            await source.repondre(question.key, option);
            setQuestion(null);
          }}
        />
      ) : null}
    </section>
  );
}
